"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Badge = Badge;
var jsx_runtime_1 = require("react/jsx-runtime");
// ================================================================
// v2/components/Badge/index.tsx
// 汎用ステータスバッジ。EventDetail / Ticket / Store で横断利用。
//
// 色はすべて theme.palette.v2 (useV2Palette) から取得する。
// variant × color の組み合わせで figma の各バッジを表現:
//   filled  : 塗り (受付中 ○ / 入金済み ✓ = info、売り切れ × = neutral)
//   subtle  : 薄塗り (受付前 🕐 / リセール出品中 等)
//   outline : 枠線のみ (抽選 / 先着 等)
//   text    : テキストのみ
// ================================================================
var material_1 = require("@mui/material");
var theme_1 = require("../../theme");
var tokens_1 = require("../../tokens");
function Badge(_a) {
    var label = _a.label, _b = _a.variant, variant = _b === void 0 ? 'subtle' : _b, _c = _a.color, color = _c === void 0 ? 'neutral' : _c, startIcon = _a.startIcon, endIcon = _a.endIcon;
    var p = (0, theme_1.useV2Palette)();
    // color トークンの解決 (塗り色 / 薄塗り色 / 前景色)
    var resolve = function () {
        switch (color) {
            case 'primary':
                return {
                    main: p.background.primary.default,
                    subtle: p.background.primary.subtle,
                    fg: p.foreground.primary.main,
                };
            case 'info':
                return {
                    main: p.background.info.default,
                    subtle: p.background.info.subtle,
                    fg: p.foreground.semantic.info,
                };
            case 'success':
                return {
                    main: p.background.secondary.default,
                    subtle: p.background.secondary.subtle,
                    fg: p.foreground.semantic.success,
                };
            case 'warning':
                return {
                    main: p.background.warning.default,
                    subtle: p.background.warning.subtle,
                    fg: p.foreground.semantic.warning,
                };
            case 'error':
                return {
                    main: p.background.error.default,
                    subtle: p.background.error.subtle,
                    fg: p.foreground.semantic.error,
                };
            case 'neutral':
            default:
                return {
                    main: p.background.neutral.default,
                    subtle: p.background.neutral.subtle,
                    fg: p.foreground.neutral.secondary,
                };
        }
    };
    var c = resolve();
    var style = (function () {
        switch (variant) {
            case 'filled':
                // 文字色は面の明度から決める。light の neutral (#E5ECEF) や
                // dark の意味色 (info #6CB6FF 等) は淡い面で、白文字だと
                // コントラストが 1.2〜2.8:1 しか出ない。
                return {
                    bg: c.main,
                    color: (0, theme_1.pickFilledInk)(c.main, p),
                    border: c.main,
                };
            case 'subtle':
                return { bg: c.subtle, color: c.fg, border: c.subtle };
            case 'outline':
                return {
                    bg: p.background.sub,
                    color: c.fg,
                    border: p.border.neutral.strong,
                };
            case 'text':
            default:
                return { bg: 'transparent', color: c.fg, border: 'transparent' };
        }
    })();
    // アイコンは常に 12px / ラベルと同色 (Figma: Size/Icon/12_XS)。
    // 色は svg セレクタに直接当てる。utoniq-app のテーマが MuiSvgIcon に
    // color: 'default' (= grey[600]) を既定で流し込むため、親の color を
    // 継承させるだけではラベル色と揃わないケースがある。
    var iconSx = {
        display: 'inline-flex',
        flexShrink: 0,
        color: style.color,
        '& svg': { fontSize: 12, display: 'block', color: style.color },
    };
    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Figma: Spacing/5xs = 2px
            gap: '2px',
            height: 20,
            px: '6px',
            borderRadius: '2px',
            backgroundColor: style.bg,
            border: "1px solid ".concat(style.border),
        }, children: [startIcon && (0, jsx_runtime_1.jsx)(material_1.Box, { sx: iconSx, children: startIcon }), (0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                    fontSize: 12,
                    fontWeight: tokens_1.fontWeight.semiBold,
                    lineHeight: '16px',
                    color: style.color,
                    whiteSpace: 'nowrap',
                }, children: label }), endIcon && (0, jsx_runtime_1.jsx)(material_1.Box, { sx: iconSx, children: endIcon })] }));
}
